import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import AddressDisplay from './AddressDisplay'
import { required } from './config'
import {
  CreateBusItemViewModel,
  CreateFerryItemViewModel,
  CreateOtherTransportItemViewModel,
  CreateTrainItemViewModel,
} from '../../Create/models'
import {
  PatchBusItemViewModel,
  PatchFerryItemViewModel,
  PatchOtherTransportItemViewModel,
  PatchTrainItemViewModel,
} from '../../Edit/models'
import useEnsureGeoPlace from '../Hooks/useEnsureGeoPlace'

import TripAddressAutocomplete, {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import { useComponentRestrictions } from 'tripPlanner/hooks/Google/useComponentRestrictions'
import {
  ADDRESS_RESPONSE_DETAILS,
  AutocompletePlaceParam,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

type CreateOrPatchItemViewModel =
  | CreateBusItemViewModel
  | PatchBusItemViewModel
  | CreateFerryItemViewModel
  | PatchFerryItemViewModel
  | CreateOtherTransportItemViewModel
  | PatchOtherTransportItemViewModel
  | CreateTrainItemViewModel
  | PatchTrainItemViewModel

type Props = {
  autocompleteTypes: AutocompletePlaceParam
  tripId: string
}

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

function StartEndAddressFields({ autocompleteTypes, tripId }: Props) {
  const methods = useFormContext<CreateOrPatchItemViewModel>()
  const { control, setValue, watch } = methods

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)
  const componentRestrictions = useComponentRestrictions(tripId)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(`${placeName}.name`, selectedOption.label)
      setValue(`${placeName}.type`, 'GEO')

      if (selectedOption.details) {
        const { geometry, formatted_address, place_id, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        setValue(`${placeName}.id`, place_id)
        setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  return (
    <>
      <Controller
        name="startPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <TripAddressAutocomplete
            {...rest}
            ref={ref}
            required
            label="Start location"
            placeholder="Search for location"
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleStartAddressChange}
            error={error?.message}
            initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {startPlace?.address && (
        <AddressDisplay title="Address" value={startPlace.address} />
      )}

      <Controller
        name="endPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <TripAddressAutocomplete
            {...rest}
            ref={ref}
            required
            label="End location"
            placeholder="Search for location"
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleEndAddressChange}
            error={error?.message}
            initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {endPlace?.address && (
        <AddressDisplay title="Address" value={endPlace.address} />
      )}
    </>
  )
}

export default StartEndAddressFields
